import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { OrderBumpOffers } from './order-bump-offers';
import { ProductTriggerSelector } from './product-trigger-selector';
import type { OrderBumpOffer, UpsellFormData } from './validation';

export const UpsellForm = () => {
  const { t } = useTranslation('upsells');
  const { id: storeId } = useParams<{ id: string }>();
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<UpsellFormData>();
  const triggerType = watch('triggerType');
  const orderBumpOffers = watch('orderBumpOffers') || [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('form.information.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Controller
            name="offerTitle"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                <Label htmlFor="offerTitle">{t('form.information.titleLabel')}</Label>
                <Input
                  id="offerTitle"
                  {...field}
                  value={field.value || ''}
                  placeholder={t('form.information.titlePlaceholder')}
                />
                {errors.offerTitle && (
                  <p className="mt-1 text-xs leading-tight text-red-600">
                    {t(errors.offerTitle.message as string) || (errors.offerTitle.message as string)}
                  </p>
                )}
              </div>
            )}
          />

          <Controller
            name="enabled"
            control={control}
            render={({ field }) => (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="enabled">{t('form.information.enabledLabel')}</Label>
                  <p className="text-muted-foreground text-xs">{t('form.information.enabledDescription')}</p>
                </div>
                <Switch id="enabled" checked={field.value} onCheckedChange={field.onChange} />
              </div>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('form.triggers.title')}</CardTitle>
          <p className="text-muted-foreground text-sm">{t('form.triggers.description')}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Controller
            name="triggerType"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                <Label htmlFor="triggerType">{t('form.triggers.triggerTypeLabel')}</Label>
                <Select
                  onValueChange={(value: 'any' | 'product') => {
                    field.onChange(value);
                    if (value === 'any') {
                      setValue('triggers', [
                        {
                          productId: null,
                          type: 'any',
                        },
                      ]);
                    }
                  }}
                  value={field.value}
                >
                  <SelectTrigger id="triggerType">
                    <SelectValue placeholder={t('form.triggers.triggerTypePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">{t('form.triggers.always')}</SelectItem>
                    <SelectItem value="product">{t('form.triggers.product')}</SelectItem>
                  </SelectContent>
                </Select>
                {errors.triggerType && (
                  <p className="mt-1 text-xs leading-tight text-red-600">
                    {t(errors.triggerType.message as string) || (errors.triggerType.message as string)}
                  </p>
                )}
                <p className="text-muted-foreground text-xs">
                  {triggerType === 'any'
                    ? t('form.triggers.alwaysDescription')
                    : t('form.triggers.productDescription')}
                </p>
              </div>
            )}
          />
          {triggerType === 'product' && (
            <Controller
              name="triggers"
              control={control}
              render={({ field }) => (
                <div className="space-y-2">
                  <Label>{t('form.triggers.triggerProductsLabel')}</Label>
                  <ProductTriggerSelector
                    storeId={storeId || ''}
                    triggers={field.value || []}
                    onChange={field.onChange}
                    error={errors.triggers?.message as string}
                  />
                </div>
              )}
            />
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{t('form.offers.orderBumpTitle')}</CardTitle>
          <p className="text-muted-foreground text-sm">{t('form.offers.orderBumpDescription')}</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {storeId && (
              <OrderBumpOffers
                offers={orderBumpOffers}
                onAddOffer={() => {
                  const newOffer: OrderBumpOffer = {
                    productId: '',
                    variantId: null,
                    priceId: null,
                    type: 'primary',
                    precheck: false,
                    quantity: 1,
                    titleTrans: {},
                    descriptionTrans: {},
                    overrideImageUrl: null,
                    displayPrice: true,
                    displayCompareAtPrice: false,
                    compareAtPriceDiscount: 20,
                    rules: [],
                  };
                  setValue('orderBumpOffers', [...orderBumpOffers, newOffer]);
                }}
                onUpdateOffer={(index, updatedOffer) => {
                  const updated = [...orderBumpOffers];
                  updated[index] = updatedOffer;
                  setValue('orderBumpOffers', updated);
                }}
                onDeleteOffer={(index) => {
                  const updated = orderBumpOffers.filter((_, i) => i !== index);
                  setValue('orderBumpOffers', updated);
                }}
                storeId={storeId}
              />
            )}
            {errors.orderBumpOffers && (
              <p className="mt-1 text-xs leading-tight text-red-600">
                {t(errors.orderBumpOffers.message as string) || (errors.orderBumpOffers.message as string)}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
